import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import {
	APIError,
	ROOT_PARENT_ID,
	type UpdateTemplateItemInput,
} from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	defaultKind,
	STAGE_KIND,
	STAGE_KINDS,
} from "@/features/stages/utils/kind.ts";
import { useCreateTemplateItem } from "../../hooks/use-create-template-item.ts";
import { useUpdateTemplateItem } from "../../hooks/use-update-template-item.ts";
import { type ItemFormValues, itemSchema } from "../../schemas/item-schema.ts";
import type { TemplateItem } from "../../types.ts";
import { childrenCount, descendantIds } from "../../utils/tree.ts";
import { ItemCombobox } from "../item-combobox";

const VIOLATION_FIELDS: Record<string, keyof ItemFormValues> = {
	name: "name",
	description: "description",
	parent_item_id: "parentId",
	kind: "kind",
};

const VIOLATION_MESSAGES: Record<string, string> = {
	parent_is_service: "Um serviço não pode ter itens abaixo dele.",
	service_has_children: "Um item com sub-itens não pode ser um serviço.",
	invalid: "Selecione um tipo válido.",
};

const REASON_MESSAGES: Record<string, string> = {
	cycle: "Um item não pode ser movido para dentro de um de seus sub-itens.",
};

export function TemplateItemDialog({
	open,
	onOpenChange,
	templateId,
	mode,
	items,
	item,
	defaultParentId,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	templateId: string;
	mode: "create" | "edit";
	items: TemplateItem[];
	item?: TemplateItem;
	defaultParentId?: string;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const createItem = useCreateTemplateItem(templateId);
	const updateItem = useUpdateTemplateItem(templateId);
	const isPending = createItem.isPending || updateItem.isPending;

	const {
		register,
		handleSubmit,
		control,
		reset,
		setError,
		setValue,
		getFieldState,
		formState: { errors },
	} = useForm<ItemFormValues>({
		resolver: zodResolver(itemSchema),
		defaultValues: { name: "", description: "", parentId: "", kind: "stage" },
	});

	useEffect(() => {
		if (open) {
			const parentId = item?.parentId ?? defaultParentId ?? "";
			reset({
				name: item?.name ?? "",
				description: item?.description ?? "",
				parentId,
				kind: item?.kind ?? defaultKind(parentId || undefined),
			});
			setFormError(null);
		}
	}, [open, item, defaultParentId, reset]);

	const serviceBlocked =
		mode === "edit" && !!item && (childrenCount(items).get(item.id) ?? 0) > 0;
	const excludeIds = new Set([
		...items
			.filter((entry) => entry.kind === "service")
			.map((entry) => entry.id),
		...(mode === "edit" && item
			? [item.id, ...descendantIds(items, item.id)]
			: []),
	]);

	function handleParentChange(next: string) {
		if (mode === "create" && !getFieldState("kind").isDirty) {
			setValue("kind", defaultKind(next || undefined));
		}
	}

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, message] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, {
						message: VIOLATION_MESSAGES[message] ?? message,
					});
					matchedField = true;
				}
			}
			if (!matchedField) {
				setFormError(REASON_MESSAGES[err.reason] || err.message || fallback);
			}
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: ItemFormValues) {
		setFormError(null);
		const description = values.description?.trim() || undefined;

		if (mode === "create") {
			try {
				await createItem.mutateAsync({
					name: values.name,
					description,
					parentItemId: values.parentId || undefined,
					kind: values.kind,
				});
				toast.success("Item adicionado com sucesso!");
				onOpenChange(false);
			} catch (err) {
				applyAPIError(err, "Houve um erro ao adicionar o item.");
			}
			return;
		}

		if (!item) return;
		const input: UpdateTemplateItemInput = {};
		if (values.name !== item.name) input.name = values.name;
		if (values.kind !== item.kind) input.kind = values.kind;
		if (description && description !== (item.description ?? "")) {
			input.description = description;
		}
		const parentId = values.parentId ?? "";
		if (parentId !== (item.parentId ?? "")) {
			input.parentItemId = parentId || ROOT_PARENT_ID;
		}
		if (Object.keys(input).length === 0) {
			toast.info("Nenhuma alteração para salvar.");
			onOpenChange(false);
			return;
		}
		try {
			await updateItem.mutateAsync({ itemId: item.id, input });
			toast.success("Item atualizado com sucesso!");
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Houve um erro ao atualizar o item.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{mode === "create" ? "Novo item" : "Editar item"}
					</DialogTitle>
					<DialogDescription>
						Itens organizam o template em uma árvore de trabalhos e
						sub-trabalhos.
					</DialogDescription>
				</DialogHeader>
				<form
					id="template-item-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					className="template-form"
				>
					<div className="form-group">
						<Label htmlFor="item-name">Nome</Label>
						<Input
							id="item-name"
							placeholder="Ex.: Fundação"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="item-description">Descrição</Label>
						<Textarea
							id="item-description"
							placeholder="Detalhes do item (opcional)"
							aria-invalid={!!errors.description}
							{...register("description")}
						/>
						{mode === "edit" && item?.description && (
							<p className="template-form__hint">
								A descrição não pode ser removida, apenas alterada.
							</p>
						)}
						{errors.description && (
							<p className="form-error">{errors.description.message}</p>
						)}
					</div>

					<div className="form-group">
						<Label htmlFor="item-parent">Item pai</Label>
						<Controller
							control={control}
							name="parentId"
							render={({ field }) => (
								<ItemCombobox
									id="item-parent"
									items={items}
									value={field.value ?? ""}
									onChange={(next) => {
										field.onChange(next);
										handleParentChange(next);
									}}
									excludeIds={excludeIds}
								/>
							)}
						/>
						{errors.parentId && (
							<p className="form-error">{errors.parentId.message}</p>
						)}
					</div>

					<div className="form-group">
						<Label htmlFor="item-kind">Tipo</Label>
						<Controller
							control={control}
							name="kind"
							render={({ field }) => (
								<Select value={field.value} onValueChange={field.onChange}>
									<SelectTrigger
										id="item-kind"
										className="template-form__select"
										aria-invalid={!!errors.kind}
									>
										<SelectValue placeholder="Selecione o tipo" />
									</SelectTrigger>
									<SelectContent>
										{STAGE_KINDS.map((option) => (
											<SelectItem
												key={option}
												value={option}
												disabled={option === "service" && serviceBlocked}
											>
												{STAGE_KIND[option].label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
						{errors.kind && <p className="form-error">{errors.kind.message}</p>}
					</div>

					{formError && (
						<p role="alert" className="template-form__error">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={isPending}
					>
						Cancelar
					</Button>
					<Button type="submit" form="template-item-form" disabled={isPending}>
						{isPending && <Loader2 className="template-spinner" />}
						{mode === "create" ? "Adicionar item" : "Salvar alterações"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
